// ABOUTME: Admin interface for managing knowledge base videos
// ABOUTME: Provides CRUD operations with upload support and engagement analytics

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Typography,
  Alert,
  SelectChangeEvent,
  Autocomplete,
} from '@mui/material';
import { Add, Edit, Delete, PlayCircle } from '@mui/icons-material';
import { getKBVideos, createKBVideo, updateKBVideo, deleteKBVideo, getKBTags } from '../../../services/api';
import { KBVideoDto, CreateKBVideoDto, UpdateKBVideoDto, KBTagDto } from '../../../types/knowledgeBase';

// Handles https://vimeo.com/ID and bare IDs. Channel/group URLs (vimeo.com/channels/…)
// and player URLs (player.vimeo.com/video/ID) are not matched and pass through unchanged.
export const extractVimeoId = (value: string): string => {
  const match = value.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : value;
};

const VideoManager: React.FC = () => {
  const [videos, setVideos] = useState<KBVideoDto[]>([]);
  const [tags, setTags] = useState<KBTagDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState<KBVideoDto | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<KBVideoDto | null>(null);
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateKBVideoDto>({
    title: '',
    description: null,
    storageProvider: 'youtube',
    storageIdentifier: '',
    durationSeconds: null,
    audienceTag: 'Both',
    accessControl: 'Authenticated',
    isUserFacingVisible: false,
    publishStatus: 'Draft',
    tagIds: [],
  });
  const [originalFormData, setOriginalFormData] = useState<CreateKBVideoDto | null>(null);

  // Helper function to detect if form has changed from original
  const hasFormChanged = (): boolean => {
    if (!editingVideo || !originalFormData) return formData.title.trim() !== '' || formData.storageIdentifier.trim() !== '';
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      const [videosData, tagsData] = await Promise.all([
        getKBVideos(),
        getKBTags(),
      ]);
      setVideos(videosData);
      setTags(tagsData);
    } catch (err) {
      console.error('Failed to load videos:', err);
      setError('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setEditingVideo(null);
    const newFormData = {
      title: '',
      description: null,
      storageProvider: 'youtube',
      storageIdentifier: '',
      durationSeconds: null,
      audienceTag: 'Both',
      accessControl: 'Authenticated',
      isUserFacingVisible: false,
      publishStatus: 'Draft',
      tagIds: [],
    };
    setFormData(newFormData);
    setOriginalFormData(null);
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (video: KBVideoDto) => {
    setEditingVideo(video);
    const editFormData = {
      title: video.title,
      description: video.description,
      storageProvider: video.storageProvider,
      storageIdentifier: video.storageIdentifier,
      durationSeconds: video.durationSeconds,
      audienceTag: video.audienceTag,
      accessControl: video.accessControl,
      isUserFacingVisible: video.isUserFacingVisible,
      publishStatus: video.publishStatus,
      tagIds: video.tags.map(t => t.id),
    };
    setFormData(editFormData);
    setOriginalFormData(editFormData);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (hasFormChanged()) {
      setUnsavedChangesDialogOpen(true);
    } else {
      setDialogOpen(false);
      setEditingVideo(null);
    }
  };

  const handleConfirmClose = () => {
    setUnsavedChangesDialogOpen(false);
    setDialogOpen(false);
    setEditingVideo(null);
  };

  const handleCancelClose = () => {
    setUnsavedChangesDialogOpen(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingVideo) {
        const updateDto: UpdateKBVideoDto = {
          title: formData.title,
          description: formData.description,
          durationSeconds: formData.durationSeconds,
          audienceTag: formData.audienceTag,
          accessControl: formData.accessControl,
          isUserFacingVisible: formData.isUserFacingVisible,
          publishStatus: formData.publishStatus,
          tagIds: formData.tagIds,
        };
        await updateKBVideo(editingVideo.id, updateDto);
      } else {
        const submitData = formData.storageProvider === 'vimeo'
          ? { ...formData, storageIdentifier: extractVimeoId(formData.storageIdentifier) }
          : formData;
        await createKBVideo(submitData);
      }
      // Close dialog directly after successful save
      setDialogOpen(false);
      setEditingVideo(null);
      loadData();
    } catch (err) {
      console.error('Failed to save video:', err);
      setError('Failed to save video');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (video: KBVideoDto) => {
    setVideoToDelete(video);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setVideoToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!videoToDelete) return;

    try {
      await deleteKBVideo(videoToDelete.id);
      handleCloseDeleteDialog();
      loadData();
    } catch (err) {
      console.error('Failed to delete video:', err);
      setError('Failed to delete video');
    }
  };

  const handleFormChange = (field: keyof CreateKBVideoDto, value: CreateKBVideoDto[keyof CreateKBVideoDto]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    handleFormChange(name as keyof CreateKBVideoDto, value);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Videos</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreateDialog}
        >
          Add Video
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Videos Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Audience</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Views</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {videos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No videos found. Add your first video to get started.
                </TableCell>
              </TableRow>
            ) : (
              videos.map((video) => (
                <TableRow key={video.id}>
                  <TableCell><strong>{video.id}</strong></TableCell>
                  <TableCell>{video.title}</TableCell>
                  <TableCell>
                    <Chip label={video.audienceTag} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={video.publishStatus}
                      color={video.publishStatus === 'Published' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{video.viewCount}</TableCell>
                  <TableCell>
                    {video.durationSeconds ? `${Math.floor(video.durationSeconds / 60)}:${(video.durationSeconds % 60).toString().padStart(2, '0')}` : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {video.tags.map((tag) => (
                      <Chip key={tag.id} label={tag.name} size="small" sx={{ mr: 0.5 }} />
                    ))}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      href={video.playbackUrl}
                      target="_blank"
                      title="Play video"
                      aria-label="Play video"
                    >
                      <PlayCircle />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditDialog(video)}
                      title="Edit video"
                      aria-label="Edit video"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDeleteDialog(video)}
                      title="Delete video"
                      aria-label="Delete video"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingVideo ? 'Edit Video' : 'Add Video'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => handleFormChange('title', e.target.value)}
              required
              fullWidth
            />

            <TextField
              label="Description"
              value={formData.description || ''}
              onChange={(e) => handleFormChange('description', e.target.value || null)}
              multiline
              rows={3}
              fullWidth
            />

            {editingVideo ? (
              <>
                <TextField
                  label="Storage Provider"
                  value={formData.storageProvider}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText="Storage provider cannot be changed after creation"
                />

                <TextField
                  label="Storage Identifier"
                  value={formData.storageIdentifier}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText="YouTube video ID, Vimeo ID, or S3 key (read-only)"
                />
              </>
            ) : (
              <>
                <FormControl fullWidth>
                  <InputLabel>Storage Provider</InputLabel>
                  <Select
                    name="storageProvider"
                    value={formData.storageProvider}
                    onChange={handleSelectChange}
                    required
                  >
                    <MenuItem value="youtube">YouTube</MenuItem>
                    <MenuItem value="vimeo">Vimeo</MenuItem>
                    <MenuItem value="s3">S3</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Storage Identifier"
                  value={formData.storageIdentifier}
                  onChange={(e) => handleFormChange('storageIdentifier', e.target.value)}
                  required
                  fullWidth
                  helperText="YouTube video ID, Vimeo ID or URL, or S3 key"
                />
              </>
            )}

            <TextField
              label="Duration (seconds)"
              type="number"
              value={formData.durationSeconds || ''}
              onChange={(e) => handleFormChange('durationSeconds', e.target.value ? parseInt(e.target.value) : null)}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Audience Tag</InputLabel>
              <Select
                name="audienceTag"
                value={formData.audienceTag}
                onChange={handleSelectChange}
                required
              >
                <MenuItem value="Parent">Parent</MenuItem>
                <MenuItem value="Child">Child</MenuItem>
                <MenuItem value="Both">Both</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Publish Status</InputLabel>
              <Select
                name="publishStatus"
                value={formData.publishStatus}
                onChange={handleSelectChange}
                required
              >
                <MenuItem value="Draft">Draft</MenuItem>
                <MenuItem value="Published">Published</MenuItem>
                <MenuItem value="Archived">Archived</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Access Control</InputLabel>
              <Select
                name="accessControl"
                value={formData.accessControl}
                onChange={handleSelectChange}
                required
              >
                <MenuItem value="Public">Public</MenuItem>
                <MenuItem value="Authenticated">Authenticated</MenuItem>
                <MenuItem value="Family">Family</MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              multiple
              options={tags}
              getOptionLabel={(option) => option.name}
              value={tags.filter(tag => formData.tagIds.includes(tag.id))}
              onChange={(_event, newValue) => {
                handleFormChange('tagIds', newValue.map(tag => tag.id));
              }}
              renderInput={(params) => (
                <TextField {...params} label="Tags" placeholder="Select tags" />
              )}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isUserFacingVisible}
                  onChange={(e) => handleFormChange('isUserFacingVisible', e.target.checked)}
                />
              }
              label="Visible to Users"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !formData.title || (!editingVideo && !formData.storageIdentifier)}
          >
            {saving ? <CircularProgress size={20} /> : (editingVideo ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Video</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{videoToDelete?.title}&quot;? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog open={unsavedChangesDialogOpen} onClose={handleCancelClose}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Are you sure you want to close without saving?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose}>Continue Editing</Button>
          <Button onClick={handleConfirmClose} color="error" variant="contained">
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VideoManager;
